/**
 * useExpedienteMessages
 * Historico de mensajes push al cliente con filtros y paginacion cursor.
 */
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getExpedienteMessages } from '@/services/expedientes.service';
import { PARTNER_ID } from '@/constants';
import type { CreditSubCategory, MessageCategory } from '@/types/expedientes.types';

export interface MessagesFilters {
  category: MessageCategory | 'all';
  subCategory: CreditSubCategory[];
  from?: number;
  to?: number;
}

const DEFAULT_FILTERS: MessagesFilters = {
  category: 'credit',
  subCategory: [],
};

const DEFAULT_PAGE_SIZE = 50;

export function useExpedienteMessages(directoryId: string | undefined, enabled: boolean) {
  const [filters, setFilters] = useState<MessagesFilters>(DEFAULT_FILTERS);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = useQuery({
    queryKey: ['expedientes', 'messages', directoryId, PARTNER_ID, filters, cursor],
    queryFn: () => {
      const subCategory = filters.subCategory.length ? filters.subCategory.join(',') : undefined;
      return getExpedienteMessages(directoryId!, PARTNER_ID, {
        ...(filters.from !== undefined && { from: filters.from }),
        ...(filters.to !== undefined && { to: filters.to }),
        category: filters.category,
        ...(subCategory !== undefined && { subCategory }),
        ...(cursor !== undefined && { cursor }),
        pageSize: DEFAULT_PAGE_SIZE,
      });
    },
    enabled: Boolean(directoryId) && enabled,
  });

  const loadNext = useCallback(() => {
    const next = query.data?.data.pageInfo.nextCursor;
    if (next) setCursor(next);
  }, [query.data]);

  const updateFilters = useCallback((patch: Partial<MessagesFilters>) => {
    setCursor(undefined);
    setFilters(prev => ({ ...prev, ...patch }));
  }, []);

  return {
    items: query.data?.data.items ?? [],
    pageSize: query.data?.data.pageInfo.pageSize ?? DEFAULT_PAGE_SIZE,
    hasNext: query.data?.data.pageInfo.hasNext ?? false,
    nextCursor: query.data?.data.pageInfo.nextCursor,
    loadNext,
    filters,
    setFilters: updateFilters,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
